import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { namespace } from '@common/config/constants';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { calculatedWaContactName } from '@common-reducers/WhatsAppSelectors';
import { getWaContactBySerializedId } from '@common-reducers/wa-contact-entity-lookup';
import type { CommonRootState } from '@common/types/common-root-state-type';
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { KIND_GLYPH, StatusRingIcon } from '@common/components/kind-glyphs/kind-glyphs';
import type { GlyphKind } from '@common/components/kind-glyphs/send-kind';
import { SLOT_GEOMETRY, planCornerBadges } from './destination-geometry';
import {
    destinationLabelParts,
    formatMore,
    planMemberList,
    planSlot,
    type DestinationPlan,
} from './destination-plan';
import styles from './destination-avatars.module.scss';

export type DestinationSize = 'row' | 'inline' | 'card';

export type DestinationHints = Readonly<Record<string, { name?: string; picture?: string } | undefined>>;

/**
 * Geometry per surface lives in destination-geometry.ts (pure, and measured by
 * its tests); the stylesheet reads it back through custom properties, so the
 * numbers ContactAvatar is given and the numbers CSS lays out with can never
 * disagree.
 *
 *  row     a sidebar row: ONE 44px slot on every row, the face with two
 *          corner badges — the count top-end, the kind bottom-end.
 *  inline  a header line ("6 contacts"): a small face and its count.
 *  card    the hover card's recipient line: the face ALONE, sized to itself.
 *          A card never draws a group — it IS the detail, so it lists the
 *          people instead (DestinationMemberList).
 */
const CARD_FACE = 22;

interface Props {
    plan: DestinationPlan;
    size: DestinationSize;
    /** What the document already carries per recipient (a message's stored
     *  contact name and picture URL). Both are HINTS, used only when the contact
     *  cache has nothing: the picture goes to ContactAvatar as its
     *  `fallbackPicURL`. It used to go in as `profilePicURL`, which OVERRIDES the
     *  cache — so a URL stored at compose time (WhatsApp picture URLs expire)
     *  beat the fresh cached one, failed to load, and the avatar then cleared
     *  the perfectly good cached picture (a server write) and drew a silhouette. */
    hints?: DestinationHints;
    /** The kind badge for the bottom-end corner (rows only): which glyph, and
     *  what it is announced as. */
    kindBadge?: { kind: GlyphKind; label: string };
}

/** Display names for these ids: the contact cache first (the same resolution
 *  ContactAvatar's own tooltip uses), then what the document stored, then the
 *  number itself. Selected as a small array of strings, so a component
 *  re-renders only when one of these names changes. */
function useRecipientNames(ids: readonly string[], hints?: DestinationHints): string[] {
    const key = ids.join('|');
    const selector = useCallback((state: CommonRootState) => {
        const entities = (state as any).DBWaContactsCacheReducer?.entities || {};
        return key ? key.split('|').map(id => {
            const contact = getWaContactBySerializedId(entities, id);
            return (contact ? calculatedWaContactName(contact) : '') || hints?.[id]?.name || '';
        }) : [];
    }, [key, hints]);
    return useAppSelector(selector) as string[];
}

/**
 * What to call a member nobody has named: a person's number ("+972501234567"),
 * never a raw `@c.us`/`@g.us` id — and for a group, a channel or a hidden-number
 * contact, what it IS, since their digits mean nothing to a reader.
 */
function useFallbackLabel(): (id: string) => React.ReactNode {
    const { t } = useTranslation(namespace);
    return (id: string) => {
        const phone = id.match(/^(\d+)@c\.us$/);
        if (phone) return <bdi dir="ltr">{`+${phone[1]}`}</bdi>;
        if (chatKindOf(id) === 'group') return t('calendar.destination.unnamedGroup');
        if (chatKindOf(id) === 'channel') return t('calendar.destination.unnamedChannel');
        return t('calendar.destination.unnamedContact');
    };
}

/**
 * The slot: WHERE a send goes, drawn once for the sidebar row, a header line
 * and the calendar hover card. What to draw is decided by `planSlot` and
 * `planCornerBadges`; this only paints it.
 *
 * Pictures resolve exactly as everywhere else: the face is a ContactAvatar,
 * which reads the contact cache by id, self-fetches a missing picture through
 * the shared batched loader, and falls back to the person / group / channel
 * silhouette. An audience member we cannot see is a ContactAvatar with no id —
 * the same silhouette, and no fetch.
 */
export const DestinationAvatars: React.FC<Props> = ({ plan, size, hints, kindBadge }) => {
    const { t, i18n } = useTranslation(namespace);
    const slot = planSlot(plan);

    const labelIds = plan.mode === 'group' ? plan.faces.filter(Boolean) : [];
    const names = useRecipientNames(labelIds, hints);

    if (!slot) return null;

    // The card is the detail view: a group there is a list, never a face.
    if (size === 'card') {
        if (plan.mode === 'group') return null;
        const cardVars = { '--dest-face': `${CARD_FACE}px` } as React.CSSProperties;
        return slot.isStatus ? (
            <span className={`${styles.root} ${styles.cardFace} ${styles.statusPlate}`} style={cardVars} role="img" aria-label={t('scheduledMessage.status.myStatus')}>
                <StatusRingIcon className={styles.statusGlyph} aria-hidden />
            </span>
        ) : (
            <span className={`${styles.root} ${styles.cardFace}`} style={cardVars}>
                <ContactAvatar contactId={slot.face} size={CARD_FACE} textSize={9}
                    fallbackPicURL={(slot.face && hints?.[slot.face]?.picture) || undefined} />
            </span>
        );
    }

    const g = SLOT_GEOMETRY[size];
    const corners = planCornerBadges(slot, size === 'row' ? kindBadge?.kind ?? null : null);
    const vars = {
        '--dest-slot-w': `${g.slotW}px`,
        '--dest-slot-h': `${g.slotH}px`,
        '--dest-face': `${g.face}px`,
        '--dest-badge': `${g.badge}px`,
        '--dest-ring-w': `${g.ring}px`,
        '--dest-text': `${g.text}px`,
        '--dest-glyph': `${g.glyph}px`,
    } as React.CSSProperties;

    // ONE accessible name for a group — "6 recipients, including A, B and C" —
    // rather than an unlabelled face and a bare number.
    let label: string | undefined;
    if (slot.isStatus) label = t('scheduledMessage.status.myStatus');
    else if (plan.mode === 'group') {
        const byId = new Map(labelIds.map((id, i) => [id, names[i]]));
        const parts = destinationLabelParts(plan, id => byId.get(id));
        const list = parts?.names.length ? formatList(parts.names, i18n.language) : '';
        label = list
            ? t('calendar.destination.recipientsIncluding', { count: plan.total, names: list })
            : t('calendar.destination.recipients', { count: plan.total });
    }
    const KindGlyph = corners.bottomEnd ? KIND_GLYPH[corners.bottomEnd.kind] : null;

    return (
        // The "+N" badge is centred on the slot's end rim, so a wide count ("+23") spills past the
        // slot. In a header line the count text follows right after it: keep a gap so they never overlap.
        <span className={`${styles.root} ${styles.slot}`} style={size === 'inline' && corners.topEnd ? { ...vars, marginInlineEnd: 10 } : vars}>
            <span className={styles.faceArea} {...(label ? { role: 'img', 'aria-label': label } : {})}>
                {slot.isStatus ? (
                    <span className={`${styles.face} ${styles.statusPlate}`}><StatusRingIcon className={styles.statusGlyph} aria-hidden /></span>
                ) : (
                    <span className={styles.face} {...(label ? { 'aria-hidden': true } : {})}>
                        <ContactAvatar
                            contactId={slot.face}
                            size={g.face}
                            textSize={size === 'row' ? 13 : 9}
                            fallbackPicURL={(slot.face && hints?.[slot.face]?.picture) || undefined}
                        />
                    </span>
                )}
            </span>
            {/* Top END corner: how many more. On the rim, never over the face. */}
            {corners.topEnd && (
                <span className={`${styles.badge} ${styles.badgeTopEnd}`} aria-hidden>
                    <span className={`${styles.badgeFace} ${styles.more}`}>
                        <span className={styles.moreText}>
                            {t('calendar.destination.more', { count: corners.topEnd.more, n: formatMore(corners.topEnd.more, i18n.language) })}
                        </span>
                    </span>
                </span>
            )}
            {/* Bottom END corner: what it is — mirrors the count above it. */}
            {corners.bottomEnd && KindGlyph && (
                <span className={`${styles.badge} ${styles.badgeBottomEnd}`}>
                    <span className={`${styles.badgeFace} ${styles.kindFace}`}>
                        <KindGlyph className={styles.kindGlyph} role="img" aria-label={kindBadge?.label} />
                    </span>
                </span>
            )}
        </span>
    );
};

/**
 * WHO is in it: a small avatar and a name per person, capped at HOVER_LIST_MAX
 * and ending "and N more" — never a 2,000-row list. Shared by the sidebar
 * popover and the calendar hover card so they cannot drift apart. It reads only
 * what is already in hand; opening it may fetch the listed pictures, through the
 * same batched loader every avatar uses — nothing on scroll or row render,
 * because it is not mounted until it is opened.
 */
export const DestinationMemberList: React.FC<{ plan: DestinationPlan; hints?: DestinationHints; showHeading?: boolean }> = ({
    plan, hints, showHeading = true,
}) => {
    const { t } = useTranslation(namespace);
    const list = planMemberList(plan);
    const names = useRecipientNames(list?.shown ?? [], hints);
    const fallbackLabel = useFallbackLabel();

    if (plan.mode === 'status') {
        return <div className={styles.memberHeading}>{t('scheduledMessage.status.myStatus')}</div>;
    }
    if (!list) return null;

    return (
        <div className={styles.members}>
            {showHeading && list.total > 1 && (
                <div className={styles.memberHeading}>{t('calendar.destination.recipients', { count: list.total })}</div>
            )}
            {list.shown.length > 0 && (
                <ul className={styles.memberList}>
                    {list.shown.map((id, i) => (
                        <li className={styles.member} key={id}>
                            <ContactAvatar contactId={id} size={22} textSize={9} fallbackPicURL={hints?.[id]?.picture || undefined} />
                            <span className={styles.memberName}>
                                {names[i] || fallbackLabel(id)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {list.rest > 0 && list.shown.length > 0 && (
                <div className={styles.memberRest}>{t('calendar.destination.andMore', { count: list.rest })}</div>
            )}
        </div>
    );
};

const POPOVER_WIDTH = 248;
const POPOVER_GAP = 8;

/**
 * The member list as a popover beside a row's slot — shown on hover of the slot
 * and on KEYBOARD focus of the row (see planner-list-row). It follows the hover
 * card's pattern: a non-interactive `role="tooltip"` in fixed viewport
 * coordinates, `pointer-events: none`, so it can never steal a click from the
 * row. It is portalled because the sidebar is a react-window list, whose
 * scroller carries `will-change: transform` — that makes it the containing
 * block for any fixed descendant and clips it to the list. The portal container
 * is the one every other overlay in the app uses (inside the extension's shadow
 * root when there is one), and the theme is read off the anchor so the popover
 * is dark exactly when the row it describes is.
 */
export const DestinationPopover: React.FC<{
    id: string;
    anchor: HTMLElement;
    plan: DestinationPlan;
    hints?: DestinationHints;
}> = ({ id, anchor, plan, hints }) => {
    const container = usePortalContainer();
    const [pos, setPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number } | null>(null);
    const [dark, setDark] = useState(false);

    useLayoutEffect(() => {
        const r = anchor.getBoundingClientRect();
        const rtl = getComputedStyle(anchor).direction === 'rtl';
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // Under the slot, aligned to its reading-start edge and clamped into
        // view; opening upwards (anchored by its BOTTOM, so its height does not
        // need guessing) when the lower half of the screen has no room for it.
        // Anchored by the slot's reading-start edge (its RIGHT edge in Hebrew),
        // so a popover narrower than its maximum still starts under the face.
        const side = rtl
            ? { right: Math.max(POPOVER_GAP, vw - r.right) }
            : { left: Math.max(POPOVER_GAP, Math.min(r.left, vw - POPOVER_WIDTH - POPOVER_GAP)) };
        setPos(r.bottom > vh * 0.62
            ? { bottom: vh - r.top + POPOVER_GAP, ...side }
            : { top: r.bottom + POPOVER_GAP, ...side });
        setDark(!!anchor.closest('.dark, [data-theme="dark"]'));
    }, [anchor]);

    if (!pos) return null;
    return createPortal(
        <div
            id={id}
            role="tooltip"
            className={`${styles.root} ${styles.popover}`}
            data-theme={dark ? 'dark' : undefined}
            dir={getComputedStyle(anchor).direction}
            style={{ top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right, maxWidth: POPOVER_WIDTH }}
        >
            <DestinationMemberList plan={plan} hints={hints} />
        </div>,
        container as Element,
    );
};

/**
 * Open-on-hover (mouse) and open-on-keyboard-focus for the member list — one
 * implementation for every surface that offers it (a sidebar row, a header
 * line). Nothing is mounted until it opens, so a list that is never opened
 * never renders and never fetches.
 */
export function useDestinationPopover(enabled: boolean) {
    const [open, setOpen] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clear = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
    const openNow = useCallback(() => { clear(); if (enabled) setOpen(true); }, [enabled]);
    const close = useCallback(() => { clear(); setOpen(false); }, []);
    useEffect(() => clear, []);
    const hoverHandlers = {
        onPointerEnter: (e: React.PointerEvent) => {
            if (!enabled || e.pointerType !== 'mouse') return;
            clear();
            timer.current = setTimeout(() => setOpen(true), 250);
        },
        onPointerLeave: close,
    };
    /** Keyboard focus only — a mouse click also focuses, and a list springing
     *  open under whatever that click opened is noise. */
    const onFocus = (e: React.FocusEvent<HTMLElement>) => {
        if (e.target === e.currentTarget && e.currentTarget.matches(':focus-visible')) openNow();
    };
    const onBlur = (e: React.FocusEvent<HTMLElement>) => { if (e.target === e.currentTarget) close(); };
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && open) { e.stopPropagation(); close(); }
    };
    return { open, openNow, close, hoverHandlers, focusHandlers: { onFocus, onBlur, onKeyDown } };
}

/**
 * The destination as a compact hint that opens the list — for a surface whose
 * slot is not already inside a focusable row (the sequence header). Focusable
 * itself, so the list is reachable from the keyboard, and linked to it with
 * `aria-describedby` while it is open.
 */
export const DestinationWithList: React.FC<{ plan: DestinationPlan; size: DestinationSize; hints?: DestinationHints }> = ({
    plan, size, hints,
}) => {
    const anchor = useRef<HTMLSpanElement>(null);
    const id = useId();
    const pop = useDestinationPopover(plan.mode !== 'none');
    if (plan.mode === 'none') return null;
    return (
        <>
            <span
                ref={anchor}
                className={styles.trigger}
                tabIndex={0}
                aria-describedby={pop.open ? id : undefined}
                {...pop.hoverHandlers}
                {...pop.focusHandlers}
            >
                <DestinationAvatars plan={plan} size={size} hints={hints} />
            </span>
            {pop.open && anchor.current && <DestinationPopover id={id} anchor={anchor.current} plan={plan} hints={hints} />}
        </>
    );
};

/** "A, B and C" in the reader's language — Intl's own list grammar, so no
 *  sentence is assembled in code. */
function formatList(names: string[], language: string): string {
    try {
        return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(names);
    } catch {
        return names.join(', ');
    }
}

export default DestinationAvatars;
